import {
  Delete,
  SettingsBackupRestore as ResetIcon,
} from "@mui/icons-material";
import {
  IconButton as MuiIconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Autocomplete,
} from "@mui/material";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import UsStatesSelector from "components/common/UsStatesSelector";
import { Fragment, useCallback, useMemo } from "react";

// The icon renders inside the fields' end adornment rather than as a sibling
// element, so the input stays the component root and its layout is untouched.
const makeResetToDefaultButton = (definition, setValue, sx) => {
  if (definition?.default == null) return null;
  return (
    <Disabled ifReadOnly>
      <IconButton
        icon={ResetIcon}
        size={"small"}
        scale={"small"}
        tooltip={"Reset to default"}
        aria-label={"Reset to default"}
        onClick={() => setValue(definition.default)}
        sx={sx}
      />
    </Disabled>
  );
};

// Option/value resolution shared by the standalone `selector` input and the
// `selector` table column, so a cell behaves exactly like a full-size selector.
const resolveSelectorOptions = (definition, value) => {
  const options = Array.isArray(definition?.options)
    ? definition.options.filter((o) => typeof o === "string")
    : [];

  // A stored value must stay visible even when it is no longer among the
  // configured options, so definition changes never hide existing data.
  // Including it in the options keeps the Autocomplete value valid.
  const selectedValue =
    typeof value === "string" && value !== "" ? value : null;

  const displayOptions =
    selectedValue !== null && !options.includes(selectedValue)
      ? [...options, selectedValue]
      : options;

  return { selectedValue, displayOptions };
};

// `onChange` covers option-select and clear. `onInputChange` is attached only
// for write-in, where it commits free text as it is typed (no Enter required);
// "reset"/"clear" reasons fire on option-select and clear and are already
// handled by `onChange`, so they are ignored.
const makeSelectorHandlers = (setValue, allowWriteIn) => ({
  onChange: (_, newValue) => setValue(newValue ?? null),
  onInputChange: allowWriteIn
    ? (_, newInputValue, reason) => {
        if (reason === "input") {
          setValue(newInputValue || null);
        }
      }
    : undefined,
});

// The in-cell form of the `selector` input: identical option/value semantics to
// `SelectorInput`, but sized and chrome-matched to the other table cells —
// small, filled and unlabelled, because the column header carries the label.
// `disabled` is forwarded explicitly: `Disabled` clones this component and
// injects `disabled` onto it, not onto the Autocomplete it renders.
const SelectorCell = ({ definition, value, setValue, disabled }) => {
  const { selectedValue, displayOptions } = resolveSelectorOptions(
    definition,
    value
  );
  const allowWriteIn = definition?.allowWriteIn === true;
  const { onChange, onInputChange } = makeSelectorHandlers(
    setValue,
    allowWriteIn
  );

  return (
    <Autocomplete
      disabled={disabled}
      size={"small"}
      value={selectedValue}
      options={displayOptions}
      freeSolo={allowWriteIn}
      forcePopupIcon
      autoHighlight
      onChange={onChange}
      onInputChange={onInputChange}
      sx={{ minWidth: 240 }}
      renderInput={(params) => (
        <TextField {...params} size={"small"} variant={"filled"} hiddenLabel />
      )}
    />
  );
};

const TableRowEntry = ({
  index,
  row,
  columns,
  rowLabel,
  onUpdate,
  onDelete,
  showDeleteColumn,
  showDelete,
}) => {
  return (
    <TableRow>
      {rowLabel != null && (
        <TableCell sx={{ verticalAlign: "middle", fontWeight: 500 }}>
          {rowLabel}
        </TableCell>
      )}
      {columns.map((col) => (
        <TableCell key={col.key}>
          {/* Disabled must wrap the input itself: cloneElement only injects
              `disabled` onto its direct children, so a wrapper around the
              whole table never reaches these fields. */}
          <Disabled ifReadOnly>
            {col.type === "number" ? (
              <PrettyNumberTextField
                size={"small"}
                variant={"filled"}
                hiddenLabel
                value={row?.[col.key] ?? ""}
                onChangeNumber={(v) => onUpdate({ ...row, [col.key]: v })}
                InputProps={{ spellCheck: "false" }}
                inputProps={{ style: { textAlign: "right" } }}
              />
            ) : col.type === "selector" ? (
              <SelectorCell
                definition={col}
                value={row?.[col.key] ?? ""}
                setValue={(v) => onUpdate({ ...row, [col.key]: v })}
              />
            ) : (
              <TextField
                size={"small"}
                variant={"filled"}
                hiddenLabel
                value={row?.[col.key] ?? ""}
                onChange={(e) =>
                  onUpdate({ ...row, [col.key]: e.target.value })
                }
              />
            )}
          </Disabled>
        </TableCell>
      ))}
      {showDeleteColumn && (
        <TableCell padding={"none"} sx={{ width: 40, verticalAlign: "middle" }}>
          {showDelete && (
            <Disabled ifReadOnly>
              <MuiIconButton size={"small"} onClick={onDelete}>
                <Delete />
              </MuiIconButton>
            </Disabled>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

// A column `default` is seeded only when the column's own cell will render it.
// Anything else — a number or boolean on a `selector` column, a string on a
// `number` column — draws as an empty cell but would still be persisted the
// moment a sibling cell is edited, storing a value the operator never saw.
// That is the mirror image of the fixed-row defect below, so it is refused the
// same way: if the cell cannot show it, the row does not carry it.
const isSeedableDefault = (col) => {
  const value = col?.default;
  if (value == null) return false;
  switch (col?.type) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "selector":
      // An empty string is what `blankRow` already gives, so it is not a seed.
      return typeof value === "string" && value !== "";
    default:
      return typeof value === "string" && value !== "";
  }
};

const TableInput = ({ definition, value, setValue }) => {
  const columns = useMemo(() => definition?.columns ?? [], [
    definition?.columns,
  ]);
  const rowDefs = definition?.rows;
  const isFixed = Array.isArray(rowDefs) && rowDefs.length > 0;
  const hasRowLabels =
    isFixed && rowDefs.some((r) => r?.label != null && r?.label !== "");

  const dataRows = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  // Every cell at its type's empty value. Used wherever a row is shown or
  // stored without the operator having chosen anything: the placeholder for a
  // fixed row with no data, and the padding `updateRow` inserts ahead of the
  // row being edited.
  const blankRow = useCallback(
    () =>
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col.key]: col.type === "number" ? null : "",
        }),
        {}
      ),
    [columns]
  );

  // A blank row with any column `default` applied. Only the trailing add-row of
  // a dynamic table uses this, because that row is stored as soon as the
  // operator edits it — so what they see is what gets saved. Fixed rows are
  // deliberately excluded: they are displayed before anything is stored, and
  // seeding them would show a value the submission never holds.
  const defaultRow = useCallback(
    () =>
      columns.reduce(
        (acc, col) =>
          isSeedableDefault(col) ? { ...acc, [col.key]: col.default } : acc,
        blankRow()
      ),
    [columns, blankRow]
  );

  const updateRow = useCallback(
    (index) => (newRow) => {
      if (isFixed) {
        const padded = [...dataRows];
        while (padded.length <= index) padded.push(blankRow());
        padded[index] = newRow;
        setValue(padded);
      } else {
        if (index < dataRows.length) {
          setValue(dataRows.map((r, i) => (i === index ? newRow : r)));
        } else {
          setValue([...dataRows, newRow]);
        }
      }
    },
    [dataRows, isFixed, blankRow, setValue]
  );

  const deleteRow = useCallback(
    (index) => () => {
      setValue(dataRows.filter((_, i) => i !== index));
    },
    [dataRows, setValue]
  );

  const displayRows = isFixed
    ? rowDefs.map((def, i) => ({
        label: def?.label ?? "",
        data: dataRows[i] ?? blankRow(),
      }))
    : [
        ...dataRows.map((r) => ({ label: null, data: r })),
        { label: null, data: defaultRow() },
      ];

  return (
    <>
      {definition?.label && (
        <Typography variant={"body2"} sx={{ mb: 1, fontWeight: 500 }}>
          {definition.label}
        </Typography>
      )}
      <TableContainer>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              {hasRowLabels && <TableCell>{"Label"}</TableCell>}
              {columns.map((col) => (
                <TableCell key={col.key}>{col.label ?? col.key}</TableCell>
              ))}
              {!isFixed && <TableCell sx={{ width: 40 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row, index) => (
              <TableRowEntry
                key={index}
                index={index}
                row={row.data}
                rowLabel={hasRowLabels ? row.label ?? "" : null}
                columns={columns}
                onUpdate={updateRow(index)}
                onDelete={deleteRow(index)}
                showDeleteColumn={!isFixed}
                showDelete={!isFixed && index < dataRows.length}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const NumberInput = ({ definition, value, setValue }) => {
  const resetToDefaultButton = makeResetToDefaultButton(definition, setValue);
  return (
    <Disabled ifReadOnly>
      <PrettyNumberTextField
        label={definition.label}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          spellCheck: "false",
          ...(resetToDefaultButton && {
            endAdornment: (
              <InputAdornment position={"end"}>
                {resetToDefaultButton}
              </InputAdornment>
            ),
          }),
        }}
        inputProps={{ style: { textAlign: "right" } }}
        size={"small"}
        variant={"filled"}
        sx={{ maxWidth: 200 }}
        value={value ?? ""}
        onChangeNumber={setValue}
      />
    </Disabled>
  );
};

const TextInput = ({ definition, value, setValue }) => {
  const resetToDefaultButton = makeResetToDefaultButton(definition, setValue);
  return (
    <Disabled ifReadOnly>
      <TextField
        label={definition.label}
        InputLabelProps={{ shrink: true }}
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value)}
        {...definition.textFieldProps}
        {...(resetToDefaultButton && {
          InputProps: {
            ...definition.textFieldProps?.InputProps,
            endAdornment: (
              <InputAdornment position={"end"}>
                {resetToDefaultButton}
              </InputAdornment>
            ),
          },
        })}
      />
    </Disabled>
  );
};

const StateInput = ({ definition, value, setValue }) => {
  return (
    <UsStatesSelector
      stateId={value ?? ""}
      label={definition.label ?? "State"}
      onChange={setValue}
    />
  );
};

const BooleanInput = ({ definition, value, setValue }) => {
  return (
    <Disabled ifReadOnly>
      <FormControlLabel
        control={
          <Switch
            checked={value ?? false}
            onChange={(e) => setValue(e.target.checked)}
          />
        }
        label={definition.label}
      />
    </Disabled>
  );
};

const SelectorInput = ({ definition, value, setValue }) => {
  // The Autocomplete's clear/popup indicators live in an absolutely-positioned
  // container with no public slot for extra buttons, while ours renders as a
  // flex-centered sibling in the label-padded input — nudge it up to align.
  const resetToDefaultButton = makeResetToDefaultButton(definition, setValue, {
    transform: "translateY(-9px)",
  });

  const { selectedValue, displayOptions } = resolveSelectorOptions(
    definition,
    value
  );

  const allowWriteIn = definition?.allowWriteIn === true;

  const { onChange, onInputChange } = makeSelectorHandlers(
    setValue,
    allowWriteIn
  );

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={selectedValue}
        options={displayOptions}
        freeSolo={allowWriteIn}
        forcePopupIcon
        autoHighlight
        onChange={onChange}
        onInputChange={onInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={definition?.label}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
            size={"medium"}
            InputProps={{
              ...params.InputProps,
              endAdornment: resetToDefaultButton ? (
                <>
                  {resetToDefaultButton}
                  {params.InputProps.endAdornment}
                </>
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        )}
      />
    </Disabled>
  );
};

const makeExtraInputComponent = (type) => {
  switch (type) {
    case "number":
      return NumberInput;
    case "text":
      return TextInput;
    case "usState":
      return StateInput;
    case "boolean":
      return BooleanInput;
    case "table":
      return TableInput;
    case "selector":
      return SelectorInput;
    default:
      return Fragment;
  }
};

const ExtraInput = ({ definition, value, setValue }) => {
  const ExtraInputComponent = makeExtraInputComponent(definition?.type);
  return (
    <ExtraInputComponent
      definition={definition}
      value={value}
      setValue={setValue}
    />
  );
};

export default ExtraInput;
